// src/hooks/useSearch.ts
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import type { SearchFilters, Vacancy, Resume, SearchResponse } from '@/types'
import { useDebounce } from './useDebounce'
import { useAuthStore } from '@/store'

export const useSearch = () => {
  const queryClient = useQueryClient()
  const { isAuthenticated } = useAuthStore()

  // Поиск вакансий
  const searchVacancies = (
    query: string,
    filters: SearchFilters,
    page: number = 1,
    size: number = 20
  ) => {
    return useQuery({
      queryKey: ['search', 'vacancies', query, filters, page, size],
      queryFn: async () => {
        const response = await apiClient.post<SearchResponse<Vacancy>>('/v1/search/vacancies', {
          query,
          filters,
          page,
          size,
        })
        return response
      },
      staleTime: 2 * 60 * 1000, // 2 минуты
      enabled: query.length >= 2 || Object.keys(filters).length > 0,
    })
  }

  // Поиск резюме (только для работодателей)
  const searchResumes = (
    query: string,
    filters: SearchFilters,
    page: number = 1,
    size: number = 20
  ) => {
    return useQuery({
      queryKey: ['search', 'resumes', query, filters, page, size],
      queryFn: async () => {
        const response = await apiClient.post<SearchResponse<Resume>>('/v1/search/resumes', {
          query,
          filters,
          page,
          size,
        })
        return response
      },
      staleTime: 2 * 60 * 1000,
      enabled: isAuthenticated && (query.length >= 2 || Object.keys(filters).length > 0),
    })
  }

  // Автодополнение
  const getAutocomplete = (query: string) => {
    const debouncedQuery = useDebounce(query, 300)

    return useQuery({
      queryKey: ['autocomplete', debouncedQuery],
      queryFn: async () => {
        const response = await apiClient.get('/v1/search/autocomplete', {
          params: { query: debouncedQuery, limit: 10 },
        })
        return response
      },
      enabled: debouncedQuery.length >= 2,
      staleTime: 60 * 1000, // 1 минута
    })
  }

  // Получение похожих вакансий
  const getSimilarVacancies = (vacancyId: number) => {
    return useQuery({
      queryKey: ['vacancies', vacancyId, 'similar'],
      queryFn: async () => {
        const response = await apiClient.get<Vacancy[]>(`/v1/vacancies/${vacancyId}/similar`)
        return response
      },
      enabled: !!vacancyId,
      staleTime: 10 * 60 * 1000,
    })
  }

  // Инвалидация поиска
  const invalidateSearch = () => {
    queryClient.invalidateQueries({ queryKey: ['search'] })
  }

  return {
    searchVacancies,
    searchResumes,
    getAutocomplete,
    getSimilarVacancies,
    invalidateSearch,
  }
}
